<template>
  <Modal
    :modal-types="[EDIT_CHART_NAME_MODAL]"
    font-awesome-icon-class="fas fa-fw fa-pencil-alt"
    modal-title="Edit Chart Name"
    :should-close-modal-on-background-click="false"
  >
    <form
      class="edit-form"
      @submit.prevent="handleEditChartNameFormSubmit()"
    >
      <label class="sc3-form-label" for="edit-chart-name-modal-chart-name-input">CHART NAME</label>
      <input
        type="text"
        placeholder="Name Your Saved Chart"
        id="edit-chart-name-modal-chart-name-input"
        class="sc3-text-input"
        v-model="chartName"
        autocomplete="off"
        :disabled="isSavingChart"
      />
      <hr class="sc3-form-divider" />
      <div v-show="errorMessage" class="error-message">{{ errorMessage }}</div>
      <button
        type="submit"
        class="sc3-button sc3-button-green"
        :disabled="isSavingChart"
      >
        <i v-show="!isSavingChart" class="fas fa-fw fa-check-circle"></i>
        <i v-show="isSavingChart" class="fad fa-fw fa-spinner-third fa-spin"></i>
        &nbsp;Update Chart Name
      </button>
      <button
        type="button"
        class="sc3-button-link"
        @click="closeModal()"
      >
        Cancel
      </button>
    </form>
  </Modal>
</template>

<script lang="ts" setup>
  import { ref, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useLayoutStore } from '@/stores/layoutStore';
  import { useSavedChartsStore } from '@/stores/savedChartsStore';
  import { useActionResultMessages } from '@/composables/useActionResultMessages';
  import { useModals } from '@/composables/useModals';
  import { useChartNames } from '@/composables/useChartNames';
  import { getChart } from '@/composables/getChart';
  import { INVALID_CHART_RECID, isValidChartRecid } from '@/composables/isValidChartRecid';
  import { areObjectsEqual } from '@/utils/areObjectsEqual';
  import Modal from '@/components/modals/Modal.vue';

  const layoutStore = useLayoutStore();
  const savedChartsStore = useSavedChartsStore();
  const { modalType } = storeToRefs(layoutStore);
  const { recidOfChartBeingEditedInModalView } = storeToRefs(savedChartsStore);
  const { addRedActionResultMessage } = useActionResultMessages();
  const { EDIT_CHART_NAME_MODAL, closeModal } = useModals();
  const { setChartName } = useChartNames();
  const isSavingChart = ref(false);
  let lastModalOpenedTime = -1;
  let lastRecidOfChartBeingEditedInModalViewChangedTime = -1;
  const errorMessage = ref('');
  const chartName = ref('');

  interface EditChartNameModalState {
    modalType: string;
    lastModalOpenedTime: number;
    recidOfChartBeingEditedInModalView: number;
    lastRecidOfChartBeingEditedInModalViewChangedTime: number;
  };

  async function handleEditChartNameFormSubmit() {
    if (
      isSavingChart.value ||
      modalType.value !== EDIT_CHART_NAME_MODAL
    ) {
      return;
    }

    isSavingChart.value = true;

    clearErrorMessage();

    const modalState = getModalState();

    if (!isValidChartRecid(recidOfChartBeingEditedInModalView.value)) {
      window.sc3WorkbenchLogger.error(`Unable to edit chart name from EditChartNameModal - recidOfChartBeingEditedInModalView is invalid: ${recidOfChartBeingEditedInModalView.value}.`);
      closeModal();
      addRedActionResultMessage('Failed to update chart.');
      return;
    }

    const chart = await getChart(recidOfChartBeingEditedInModalView.value, true);

    if (hasModalStateChanged(modalState)) {
      return;
    }

    if (!chart) {
      window.sc3WorkbenchLogger.error(`Unable to edit chart name from EditChartNameModal - no chart found with recid: ${recidOfChartBeingEditedInModalView.value}.`);
      closeModal();
      addRedActionResultMessage('Failed to update chart.');
      return;
    }

    const chartList = savedChartsStore.chartLists.find(cl => cl.listNum === chart.chartListListNum);

    if (!chartList) {
      window.sc3WorkbenchLogger.error(`Unable to edit chart name from EditChartNameModal - no ChartList found with listNum: ${chart.chartListListNum}.`);
      closeModal();
      addRedActionResultMessage('Failed to update chart.');
      return;
    }

    if (chartList.isReadonly) {
      window.sc3WorkbenchLogger.error(`Unable to edit chart name from EditChartNameModal - ChartList with listNum: ${chart.chartListListNum} is readonly.`);
      closeModal();
      addRedActionResultMessage('Failed to update chart.');
      return;
    }

    const name = typeof chartName.value === 'string' ? chartName.value.trim() : '';

    if (!name.length || name.length > 100) {
      errorMessage.value = 'Chart name must be between 1 and 100 characters.';
      isSavingChart.value = false;
      return;
    }

    await setChartName(chart, name);

    if (!hasModalStateChanged(modalState)) {
      closeModal();
    }
  }

  function getModalState(): EditChartNameModalState {
    return {
      modalType: modalType.value,
      lastModalOpenedTime,
      recidOfChartBeingEditedInModalView: recidOfChartBeingEditedInModalView.value,
      lastRecidOfChartBeingEditedInModalViewChangedTime
    };
  }

  function hasModalStateChanged(modalState: EditChartNameModalState) {
    const currentModalState = getModalState();

    return !areObjectsEqual(modalState, currentModalState);
  }

  async function handleEditChartNameModalOpened() {
    chartName.value = '';

    clearErrorMessage();

    const modalState = getModalState();
    const chart = await getChart(recidOfChartBeingEditedInModalView.value, true);

    if (
      chart &&
      !hasModalStateChanged(modalState) &&
      !chartName.value
    ) {
      chartName.value = chart.name;
    }
  }

  function handleEditChartNameModalClosed(isReInitializingModal: boolean) {
    isSavingChart.value = false;

    if (!isReInitializingModal) {
      savedChartsStore.$patch({ recidOfChartBeingEditedInModalView: INVALID_CHART_RECID });
    }
  }

  async function handleRecidOfChartBeingEditedInModalViewChanged() {
    lastRecidOfChartBeingEditedInModalViewChangedTime = Date.now();

    handleEditChartNameModalClosed(true);

    if (modalType.value !== EDIT_CHART_NAME_MODAL) {
      return;
    }

    await handleEditChartNameModalOpened();
  }

  async function handleModalTypeChanged() {
    if (modalType.value !== EDIT_CHART_NAME_MODAL) {
      handleEditChartNameModalClosed(false);
      return;
    }

    lastModalOpenedTime = Date.now();

    await handleEditChartNameModalOpened();
  }

  function clearErrorMessage() {
    errorMessage.value = '';
  }

  watch(chartName, clearErrorMessage);
  watch(recidOfChartBeingEditedInModalView, handleRecidOfChartBeingEditedInModalViewChanged);
  watch(modalType, handleModalTypeChanged);
</script>

<style scoped>
  .edit-form {
    margin: 0px;
    padding: 0px;
    text-align: center;
  }

  .error-message {
    border: 1px solid #f5c6cb;
    color: #721c24;
    background-color: #f8d7da;
    font-weight: 700;
    border-radius: 4px;
    margin: 0px 0px 8px 0px;
    line-height: 2;
    font-size: 16px;
    padding: 0px;
  }
</style>
